import { createContext, useCallback, useContext } from 'react'
import { useSelector } from '@xstate/react'
import type { ActorRefFrom, SnapshotFrom } from 'xstate'
import { prescriptionFlowMachine } from './machine'

type PrescriptionFlowActor = ActorRefFrom<typeof prescriptionFlowMachine>
type PrescriptionFlowSnapshot = SnapshotFrom<typeof prescriptionFlowMachine>

const PrescriptionFlowActorContext = createContext<PrescriptionFlowActor | null>(null)

/** Leaves the prescription flow for the kiosk hub ("Zurück zum Start"). */
const PrescriptionFlowExitContext = createContext<(() => void) | null>(null)

/**
 * Provider component — wraps the flow and provides the actor to children
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PrescriptionFlowActorProvider actor={actor}>
 *   <PrescriptionStep />
 * </PrescriptionFlowActorProvider>
 * ```
 */
export function PrescriptionFlowActorProvider({
  actor,
  children,
  onExit,
}: {
  actor: PrescriptionFlowActor
  children: React.ReactNode
  /** Leaves the flow for the kiosk hub; defaults to resetting the flow. */
  onExit?: () => void
}) {
  return (
    <PrescriptionFlowActorContext.Provider value={actor}>
      <PrescriptionFlowExitContext.Provider value={onExit ?? null}>
        {children}
      </PrescriptionFlowExitContext.Provider>
    </PrescriptionFlowActorContext.Provider>
  )
}

/**
 * Returns the action behind every "Zurück zum Start" in the prescription
 * flow: leave for the kiosk hub. Without a flow-level exit (isolated stories
 * and tests) it resets the flow to its first step.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const exitToStart = usePrescriptionExitToStart()
 * ```
 */
export function usePrescriptionExitToStart(): () => void {
  const actor = usePrescriptionFlowActor()
  const onExit = useContext(PrescriptionFlowExitContext)
  return useCallback(function exitPrescriptionToStart() {
    if (onExit) onExit()
    else actor.send({ type: 'DONE' })
  }, [actor, onExit])
}

/**
 * Hook to get the prescription flow actor for sending events
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const actor = usePrescriptionFlowActor()
 * ```
 */
function usePrescriptionFlowActor(): PrescriptionFlowActor {
  const actor = useContext(PrescriptionFlowActorContext)
  if (!actor) {
    throw new Error('usePrescriptionFlowActor must be used within PrescriptionFlowActorProvider')
  }
  return actor
}

/**
 * Hook to send events to the prescription flow machine
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const send = usePrescriptionFlowSend()
 * send({ type: 'START' })
 * ```
 */
export function usePrescriptionFlowSend() {
  const actor = usePrescriptionFlowActor()
  return actor.send
}

/**
 * Hook to select specific parts of the prescription flow state
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const state = usePrescriptionFlowSelector(prescriptionFlowSelectors.currentState)
 * ```
 */
export function usePrescriptionFlowSelector<T>(
  selector: (snapshot: PrescriptionFlowSnapshot) => T
): T {
  const actor = usePrescriptionFlowActor()
  return useSelector(actor, selector)
}
